import { useState } from 'react';

import { nextMonday, startOfMonth } from 'date-fns';
import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';

import { AdditionalCapacityWithoutId, Scenario } from 'components/types';

import { ScenarioAdditionalCapacityType, Units } from 'components/enums';

import { getStartDateFromWeekNumber, toLocalISOString } from 'utils/date';
import { displayNumber } from 'utils/misc';

import { CapacityBalanceResponse } from 'RestAPIs/roster/formation';

import { scenarioAtom } from '../../../../atoms';
import { showPerFunctionAtom, updateFlexForecastAtom } from '../../../atoms';

import {
  Collapse,
  IconButton,
  styled,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';

import { NumericTableCell } from 'components/shared/Table';

import AddEditAdditionalCapacityDialog from '../../../components/AddEditAdditionalCapacityDialog';

import DetailTable from './DetailTable';
import { Icon } from '@sdb/planning-ui';

const UnderlinedNumericTableCell = styled(NumericTableCell)`
  text-decoration: underline;
  cursor: pointer;
`;

type AdditionalCapacityWithoutFunction = Omit<
  AdditionalCapacityWithoutId,
  'function'
>;

type Props = {
  capacityBalance: CapacityBalanceResponse;
};

const CollapsibleCapacityBalanceTableRow = (props: Props) => {
  const { capacityBalance } = props;
  const { totalYearHoursBalance, periodHourBalances } = capacityBalance;

  const { t } = useTranslation('common');
  const [isExpanded, setIsExpanded] = useRecoilState(showPerFunctionAtom);

  const scenario = useRecoilValue(scenarioAtom) as Scenario;
  const [, setUpdateFlexForecast] = useRecoilState(updateFlexForecastAtom);
  const [
    shouldShowAddAdditionalCapacityDialog,
    setShouldShowAddAdditionalCapacityDialog
  ] = useState<boolean>(false);
  const [dataForAdditionalCapacity, setDataForAdditionalCapacity] = useState<
    AdditionalCapacityWithoutId | AdditionalCapacityWithoutFunction | null
  >(null);
  const { isViewPerWeek } = useYearQuarterNavigation();

  const onClickCell = (e, period) => {
    e.stopPropagation();
    const { hours, year, number } = period;

    const startDate = isViewPerWeek
      ? getStartDateFromWeekNumber(number, year)
      : startOfMonth(new Date(year, number - 1));
    const endDate = isViewPerWeek
      ? nextMonday(startDate)
      : startOfMonth(new Date(year, number));

    const baseData: AdditionalCapacityWithoutFunction = {
      startDate: toLocalISOString(startDate),
      endDate: toLocalISOString(endDate),
      type:
        hours > 0
          ? ScenarioAdditionalCapacityType.LOAN
          : ScenarioAdditionalCapacityType.FLEX,
      unit: isViewPerWeek ? Units.WEEK : Units.MONTH,
      hours: Math.abs(hours),
      quantity: 1,
      scenarioId: scenario.scenarioId,
      description: '',
      organizationalUnit: null
    };

    setDataForAdditionalCapacity(baseData);
    setShouldShowAddAdditionalCapacityDialog(true);
  };

  const handleCloseAdditionalCapacity = () => {
    setDataForAdditionalCapacity(null);
    setShouldShowAddAdditionalCapacityDialog(false);
  };

  const onSaveAdditionalCapacity = () => {
    handleCloseAdditionalCapacity();
    setUpdateFlexForecast(true);
  };

  return (
    <>
      {capacityBalance.functionCapacityBalances && (
        <>
          <TableRow
            sx={{ cursor: 'pointer' }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <TableCell width={200}>
              <IconButton
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ mr: 1 }}
                size="large"
              >
                <Icon
                  type={isExpanded ? 'chevron-up' : 'chevron-down'}
                  fontSize="small"
                />
              </IconButton>
              {`${t('formation.formation')} ${t('comparedTo')} ${t(
                'analyzer.filters.workDemand'
              )}`}
            </TableCell>
            {periodHourBalances.map((period) =>
              !scenario.readOnly ? (
                <UnderlinedNumericTableCell
                  key={`${period.number}`}
                  onClick={(e) => onClickCell(e, period)}
                >
                  {displayNumber(Math.round(period.hours))}
                </UnderlinedNumericTableCell>
              ) : (
                <NumericTableCell key={`${period.number}`}>
                  {displayNumber(Math.round(period.hours))}
                </NumericTableCell>
              )
            )}
            <NumericTableCell width={68}>
              {displayNumber(Math.round(totalYearHoursBalance))}
            </NumericTableCell>
          </TableRow>
          <TableRow>
            <TableCell
              sx={{ padding: 0 }}
              colSpan={9 + periodHourBalances.length}
            >
              <Collapse in={isExpanded} unmountOnExit>
                <TableContainer sx={{ maxHeight: '30vh', overflow: 'auto' }}>
                  <DetailTable
                    functionCapacityBalances={
                      capacityBalance.functionCapacityBalances
                    }
                  />
                </TableContainer>
              </Collapse>
            </TableCell>
          </TableRow>
          {shouldShowAddAdditionalCapacityDialog &&
            dataForAdditionalCapacity && (
              <AddEditAdditionalCapacityDialog
                isOpen={shouldShowAddAdditionalCapacityDialog}
                handleClose={handleCloseAdditionalCapacity}
                onSave={onSaveAdditionalCapacity}
                capacity={dataForAdditionalCapacity}
              />
            )}
        </>
      )}
    </>
  );
};

export default CollapsibleCapacityBalanceTableRow;
